// ─────────────────────────────────────────────────────────────
//  Reporting-Mails (Settings)
//
//  Globale Einstellungen für die Reporting-Erinnerungen: Admin-Adresse,
//  ab wann ein offenes Reporting eskaliert und ob überhaupt gesendet wird.
//  WER pro Kunde erinnert wird, steht in der Sales Pipeline
//  (Kunde → Deal & Team → Reporting-Erinnerungen).
//
//  Zusätzlich: SMTP-Check bzw. Testmail, damit sich der Versand ohne einen
//  echten Reporting-Fall prüfen lässt, plus eine Übersicht aller laufenden
//  Verträge und ihrer Empfänger – so fällt sofort auf, wo niemand hinterlegt ist.
// ─────────────────────────────────────────────────────────────
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { staggerContainer, itemVariants } from './dashboardMotion';
import { database, auth } from '../firebaseConfig';
import { ref, onValue, update, serverTimestamp } from 'firebase/database';
import reportingMail from './reportingMailCore';
import './TeamSettings.css';
import './ReportingMailSettings.css';

const TEAM_PATH = 'salesgod-research/team';
const CUSTOMERS_PATH = 'salesgod-research/customers';
const TEST_MAIL_FN_URL =
  process.env.REACT_APP_REPORTING_TEST_MAIL_FN_URL ||
  'https://us-central1-bc-landingpage.cloudfunctions.net/sendReportingTestMail';

const toDraft = (s) => ({
  enabled: s.enabled !== false,
  adminEmail: s.adminEmail || '',
  escalationDays: String(s.escalationDays),
  maxReminders: String(s.maxReminders),
});

const intOr = (v, fallback) => {
  const n = Math.round(Number(String(v).replace(/[^\d]/g, '')));
  return isFinite(n) && n > 0 ? n : fallback;
};

const ReportingMailSettings = () => {
  const [saved, setSaved] = useState(() => reportingMail.reportingMailSettings(null));
  const [draft, setDraft] = useState(() => toDraft(reportingMail.reportingMailSettings(null)));
  // Solange nichts angefasst wurde, folgt das Formular dem DB-Stand.
  const touchedRef = useRef(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [customers, setCustomers] = useState({});
  const [team, setTeam] = useState({});
  const [testTo, setTestTo] = useState('');
  const [testBusy, setTestBusy] = useState('');
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    const unsub = onValue(ref(database, reportingMail.REPORTING_MAIL_SETTINGS_PATH), (snap) => {
      const next = reportingMail.reportingMailSettings(snap.val());
      setSaved(next);
      // Tippen nicht überschreiben, wenn parallel gespeichert wurde.
      if (!touchedRef.current) setDraft(toDraft(next));
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    const unsubC = onValue(ref(database, CUSTOMERS_PATH), (snap) => setCustomers(snap.val() || {}));
    const unsubT = onValue(ref(database, TEAM_PATH), (snap) => setTeam(snap.val() || {}));
    return () => { unsubC(); unsubT(); };
  }, []);

  const setField = (field, value) => {
    touchedRef.current = true;
    setError('');
    setDraft((p) => ({ ...p, [field]: value }));
  };

  const dirty = JSON.stringify(toDraft(saved)) !== JSON.stringify(draft);

  const save = async () => {
    const adminEmail = reportingMail.cleanEmail(draft.adminEmail);
    if (adminEmail && !reportingMail.isEmail(adminEmail)) {
      setError('Die Admin-Adresse ist keine gültige E-Mail-Adresse.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await update(ref(database, reportingMail.REPORTING_MAIL_SETTINGS_PATH), {
        enabled: draft.enabled !== false,
        adminEmail,
        escalationDays: intOr(draft.escalationDays, reportingMail.DEFAULT_ESCALATION_DAYS),
        maxReminders: intOr(draft.maxReminders, reportingMail.DEFAULT_MAX_REMINDERS),
        updatedAt: serverTimestamp(),
      });
      touchedRef.current = false;
    } catch (e) {
      setError(e.message || 'Speichern fehlgeschlagen.');
    } finally {
      setSaving(false);
    }
  };

  // Testmail bzw. reiner Verbindungscheck laufen über dieselbe, admin-
  // authentifizierte Cloud Function (nur dort liegen die SMTP-Zugangsdaten).
  const runTest = async (verifyOnly) => {
    setTestBusy(verifyOnly ? 'verify' : 'send');
    setTestResult(null);
    try {
      const token = auth.currentUser ? await auth.currentUser.getIdToken() : null;
      if (!token) throw new Error('Nicht eingeloggt – bitte neu anmelden.');
      const resp = await fetch(TEST_MAIL_FN_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(verifyOnly ? { verifyOnly: true } : { to: testTo || undefined }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok || !data.ok) throw new Error(data.error || `Fehlgeschlagen (HTTP ${resp.status}).`);
      setTestResult({
        tone: 'ok',
        text: verifyOnly
          ? `SMTP-Verbindung steht (${data.transport.host}:${data.transport.port}, Absender ${data.transport.from}).`
          : `Testmail an ${data.to} verschickt.`,
      });
    } catch (e) {
      setTestResult({ tone: 'error', text: e.message || 'Fehlgeschlagen.' });
    } finally {
      setTestBusy('');
    }
  };

  // Übersicht: alle Kunden mit laufendem Vertrag – dort können Reportings
  // anfallen, also braucht es Empfänger.
  const rows = useMemo(() => Object.keys(customers)
    .filter((slug) => {
      const c = customers[slug] || {};
      return !!(c.contract && c.contract.active);
    })
    .map((slug) => {
      const c = customers[slug];
      const resolved = reportingMail.resolveRecipients(c, team);
      return { slug, companyName: c.companyName || slug, ...resolved };
    })
    .sort((a, b) => {
      const rank = (r) => (r.to.length === 0 ? 0 : (!r.enabled ? 1 : (r.fallback ? 2 : 3)));
      if (rank(a) !== rank(b)) return rank(a) - rank(b);
      return a.companyName.localeCompare(b.companyName);
    }), [customers, team]);

  const missing = rows.filter((r) => r.to.length === 0 || !r.enabled).length;

  return (
    <div className="ts-wrap">
      <motion.div className="ts-inner" variants={staggerContainer} initial="initial" animate="animate">
        <motion.header className="ts-head" variants={itemVariants}>
          <div>
            <h1>Reporting-Mails</h1>
            <p>
              Am Fälligkeitstag eines Reportings und danach jeden Tag geht eine Erinnerung an die
              zuständigen Teammitglieder – bis das Reporting eingereicht ist, dann folgt eine Bestätigung.
              Wer pro Kunde erinnert wird, legst du in der Sales Pipeline unter <strong>Deal &amp; Team</strong> fest.
            </p>
          </div>
        </motion.header>

        <motion.section className="ts-card" variants={itemVariants}>
          <div className="ts-card-top">
            <span className={`ts-badge ${draft.enabled ? 'ts-badge-com' : 'ts-badge-fix'}`}>
              {draft.enabled ? 'Erinnerungen aktiv' : 'Erinnerungen aus'}
            </span>
            <label className="ts-toggle">
              <input type="checkbox" checked={draft.enabled} onChange={(e) => setField('enabled', e.target.checked)} />
              <span>{draft.enabled ? 'aktiv' : 'aus'}</span>
            </label>
          </div>

          <div className="ts-row">
            <label className="ts-field ts-field-grow">
              <span>Admin-Adresse</span>
              <input
                type="email"
                value={draft.adminEmail}
                placeholder="admin@firma.com"
                onChange={(e) => setField('adminEmail', e.target.value)}
              />
              <small className="ts-field-hint">
                Bekommt jede Bestätigung in CC und eine tägliche Sammel-Info über Reportings, die
                länger als {intOr(draft.escalationDays, reportingMail.DEFAULT_ESCALATION_DAYS)} Tage offen sind.
              </small>
            </label>
          </div>

          <div className="ts-row">
            <label className="ts-field">
              <span>Info an Admin nach</span>
              <div className="ts-input-suffix">
                <input
                  type="text"
                  inputMode="numeric"
                  value={draft.escalationDays}
                  placeholder={String(reportingMail.DEFAULT_ESCALATION_DAYS)}
                  onChange={(e) => setField('escalationDays', e.target.value)}
                />
                <span>Tagen</span>
              </div>
              <small className="ts-field-hint">Verzug ab dem Fälligkeitstag gerechnet.</small>
            </label>
            <label className="ts-field">
              <span>Maximal Erinnerungen</span>
              <div className="ts-input-suffix">
                <input
                  type="text"
                  inputMode="numeric"
                  value={draft.maxReminders}
                  placeholder={String(reportingMail.DEFAULT_MAX_REMINDERS)}
                  onChange={(e) => setField('maxReminders', e.target.value)}
                />
                <span>pro Reporting</span>
              </div>
              <small className="ts-field-hint">Notbremse gegen Dauer-Mails bei Altlasten.</small>
            </label>
          </div>

          <div className="ts-card-foot">
            {error ? <span className="rm-msg rm-msg-error">{error}</span> : <span className="rm-hint">Läuft täglich um 10:00 (Europe/Vienna).</span>}
            <button type="button" className="ts-btn ts-btn-primary" onClick={save} disabled={!dirty || saving}>
              {saving ? 'Speichert…' : dirty ? 'Speichern' : 'Gespeichert'}
            </button>
          </div>
        </motion.section>

        <motion.section className="ts-card" variants={itemVariants}>
          <div className="ts-card-title">Versand testen</div>
          <p className="rm-note">
            Versendet wird über das Postfach aus <code>functions/.env</code>{' '}
            (<code>EMAIL_NODE</code> / <code>PW_EMAIL_NODE</code>), hier absichtlich nicht sichtbar.
            Prüfe damit, ob Verbindung und Absender stimmen.
          </p>
          <div className="ts-row">
            <label className="ts-field ts-field-grow">
              <span>Testmail an</span>
              <input
                type="email"
                value={testTo}
                placeholder={draft.adminEmail || 'name@firma.com'}
                onChange={(e) => setTestTo(e.target.value)}
              />
              <small className="ts-field-hint">Leer lassen = an die Admin-Adresse.</small>
            </label>
          </div>
          <div className="ts-card-foot">
            {testResult
              ? <span className={`rm-msg ${testResult.tone === 'ok' ? 'rm-msg-ok' : 'rm-msg-error'}`}>{testResult.text}</span>
              : <span className="rm-hint">Es wird nichts an Kunden verschickt.</span>}
            <div className="ts-card-actions">
              <button type="button" className="ts-btn ts-btn-ghost" onClick={() => runTest(true)} disabled={!!testBusy}>
                {testBusy === 'verify' ? 'Prüfe…' : 'Verbindung prüfen'}
              </button>
              <button type="button" className="ts-btn ts-btn-primary" onClick={() => runTest(false)} disabled={!!testBusy}>
                {testBusy === 'send' ? 'Sendet…' : 'Testmail senden'}
              </button>
            </div>
          </div>
        </motion.section>

        <motion.div className="ts-list-head" variants={itemVariants}>
          Laufende Verträge ({rows.length}){missing > 0 && <span className="rm-warn-count">{missing} ohne Empfänger</span>}
        </motion.div>

        {rows.length === 0 ? (
          <div className="ts-empty">Kein Kunde mit aktivem Vertrag – es fallen noch keine Reportings an.</div>
        ) : (
          <motion.section className="ts-card rm-list" variants={itemVariants}>
            {rows.map((r) => {
              const state = r.to.length === 0 ? 'missing' : (!r.enabled ? 'off' : (r.fallback ? 'fallback' : 'ok'));
              const label = {
                missing: 'kein Empfänger',
                off: 'ausgeschaltet',
                fallback: 'Fallback: Outreach-Manager',
                ok: `${r.to.length} Empfänger`,
              }[state];
              return (
                <div key={r.slug} className={`rm-row rm-row-${state}`}>
                  <span className="rm-row-name">{r.companyName}</span>
                  <span className="rm-row-mails">{r.to.length ? r.to.join(', ') : 'In der Pipeline unter Deal & Team auswählen'}</span>
                  <span className={`rm-row-tag rm-row-tag-${state}`}>{label}</span>
                </div>
              );
            })}
          </motion.section>
        )}
      </motion.div>
    </div>
  );
};

export default ReportingMailSettings;
